/* eslint-disable no-debugger, no-console, padding-line-between-statements */
import * as fs from "fs";
import env from "./utils/env.config";

const apiCall = async (url: string, APIVersion: string = env.apiV1) => {
  const res = await fetch(`${env.baseUrl}${url}`, {
    headers: {
      version: APIVersion,
    },
  });

  return res;
};

export const waitForMs = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export const fetchCall = async (
  url: string,
  APIVersion: string = env.apiV1,
  itemId: number | undefined = undefined
) => {
  const filePath = "backendCalls.json";
  let result: any = "N/A";
  const exists = await fs.existsSync(filePath);
  if (exists && process.env.NODE_ENV !== "development") {
    const cachedApis = await fs.readFileSync(filePath, "utf-8");
    const obj = await JSON.parse(cachedApis as unknown as string);
    const cachedItems = obj[`${url}/allItems`];
    // A cached restaurant whose item list is missing or malformed is not a
    // usable hit -- fetch it again rather than .find on it.
    if (obj[url] && (!itemId || Array.isArray(cachedItems))) {
      if (itemId) {
        const itemData = cachedItems.find((item: any) => item.id === itemId);
        result = { restaurantData: obj[url], itemData };
      } else {
        result = obj[url];
      }
    } else {
      const res = await apiCall(url, APIVersion);
      const data = await res.json();
      let allItems: any[] = [];
      if (url.match(/^\/restaurant\/[0-9]*$/) && data.id) {
        allItems = await fetchAllItemsPerRestaurant(data.id);
        obj[`${url}/allItems`] = allItems;
      } else if (itemId) {
        // There is nothing to resolve the item against. Fail loudly instead of
        // prerendering an empty page for every item on the menu.
        throw new Error(
          `[apiCache] ${url} answered without an id, cannot resolve item ${itemId}: ${JSON.stringify(
            data
          ).slice(0, 200)}`
        );
      }
      obj[url] = data;
      if (itemId) {
        const itemData = allItems.find((item: any) => item.id === itemId);
        result = { restaurantData: obj[url], itemData };
      } else {
        result = obj[url];
      }
    }
    const json = JSON.stringify(obj);
    await fs.writeFileSync(filePath, json, "utf-8");
  } else {
    console.log(filePath, "NOT EXIST");
    // For lambda cache

    if (itemId) {
      const restaurantId = url.split("/")[2];
      const itemRes = await apiCall(
        `/items/item/${itemId}?restaurantId=${restaurantId}`,
        env.apiV2
      );
      const itemData = await itemRes.json();
      result = { itemData };
    } else {
      const res = await apiCall(url, APIVersion);
      const data = await res.json();
      result = data;
    }
  }
  return result;
};

const ITEMS_ATTEMPTS = 3;

/**
 * The item list, which must be an array.
 *
 * A build pulls the restaurant's entire menu -- cariboucoffeenext is 11.7MB and
 * ~20s idle -- and the eg fleet builds every restaurant at once. Under that
 * burst this answered with an error body instead of the array and .find on it
 * aborted the whole build on its first item page ("TypeError: s.find is not a
 * function"), taking down 12 of 23 eg deploys in run 34209276902.
 *
 * The retry lives HERE and nowhere else on purpose. This is the only call that
 * crashed, and it is reached only while `next build` populates
 * backendCalls.json -- never from the server, which has no such file. Every
 * other call keeps its single-request behaviour, because /api/revalidate
 * re-renders ~26 routes in every locale synchronously and each of those runs
 * getRestaurantStaticProps: a retry budget on that path multiplies into
 * minutes and timed out twelve UI suites. #4166
 */
const fetchAllItemsPerRestaurant = async (restaurantId: number) => {
  const url = `/items/list?restaurantId=${restaurantId}`;
  let lastFailure = "";

  for (let attempt = 1; attempt <= ITEMS_ATTEMPTS; attempt++) {
    try {
      const res = await apiCall(url);
      const data = await res.json();

      if (Array.isArray(data)) {
        return data;
      }

      lastFailure = `HTTP ${res.status}, ${JSON.stringify(data).slice(0, 200)}`;
    } catch (error: any) {
      lastFailure = error?.message ?? String(error);
    }

    console.log(
      `[apiCache] ${url} attempt ${attempt}/${ITEMS_ATTEMPTS}: ${lastFailure}`
    );

    if (attempt < ITEMS_ATTEMPTS) {
      await waitForMs(1000 * attempt);
    }
  }

  throw new Error(
    `[apiCache] ${url} did not return an array after ${ITEMS_ATTEMPTS} attempts: ${lastFailure}`
  );
};
